/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// NewProduct page components
import ProyekDetail from "layouts/pages/proyek/proyek-baru/components/proyek-detail";
import Media from "layouts/ecommerce/products/new-product/components/Media";
import PelaksanaProyek from "layouts/pages/proyek/proyek-baru/components/Pelaksana";

// Import variabel API
import { API_HOHOLOK } from "utils";

function getSteps() {
  return ["Proyek Baru", "Pelaksana Proyek", "Dokumen"];
}

function getStepContent(stepIndex, formData, updateStepData) {
  switch (stepIndex) {
    case 0:
      return (
        <ProyekDetail
          values={formData.ProyekBaru}
          onChange={updateStepData}
          errors={{}}
        />
      );
    case 1:
      return (
        <PelaksanaProyek
          values={formData.ProyekBaru}
          onChange={updateStepData}
        />
      );
    case 2:
      return <Media values={formData.ProyekBaru} onChange={updateStepData} />;

    default:
      return null;
  }
}

function ProyekBaru() {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const steps = getSteps();
  const isLastStep = activeStep === steps.length - 1;

  // State gabungan untuk semua step
  const [formData, setFormData] = useState({
    ProyekBaru: {
      nama_proyek: "",
      tanggal_mulai: "",
      tanggal_selesai: "",
      total_anggaran: "",
      desa: "",
      kecamatan: "",
      longitude: "",
      latitude: "",
      radiusInput: "",
      dokumen: null,
      konsultan_perencana: null,
      konsultan_pengawas: null,
      kontraktor_pelaksana: null,
      akunKontraktor: null,
      akunPengawas: null,

      // tambahkan field lain yang diperlukan oleh child
    },
  });

  // Handler untuk update data tiap step
  const updateStepData = (data) => {
    setFormData((prev) => ({
      ...prev,
      ProyekBaru: { ...prev.ProyekBaru, ...data },
    }));
  };

  // Handler submit/send
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Ambil user dari localStorage
      const userStorage = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : null;
      const pengirim_id =
        userStorage && userStorage.uid ? userStorage.uid : null;
      const idDinas =
        userStorage && userStorage.id_dinas ? userStorage.id_dinas : null;
      // Ambil hanya field yang ada dan terisi dari formData.ProyekBaru
      const allowedFields = [
        "tanggal_selesai",
        "tanggal_mulai",
        "nama_proyek",
        "total_anggaran",
        "desa",
        "kecamatan",
        "longitude",
        "latitude",
        "radiusInput",
        "konsultan_perencana",
        "konsultan_pengawas",
        "kontraktor_pelaksana",
        "akunKontraktor",
        "akunPengawas",
      ];
      const proyekBaru = formData.ProyekBaru || {};
      const filteredData = {};
      // Cek field kosong
      const emptyFields = [];
      const fieldLabels = {
        nama_proyek: "Nama Proyek",
        tanggal_mulai: "Tanggal Mulai",
        tanggal_selesai: "Tanggal Selesai",
        total_anggaran: "Total Anggaran",
        desa: "Desa",
        kecamatan: "Kecamatan",
        longitude: "Longitude",
        latitude: "Latitude",
        radiusInput: "Radius Input",
        konsultan_perencana: "Konsultan Perencana",
        konsultan_pengawas: "Konsultan Pengawas",
        kontraktor_pelaksana: "Kontraktor Pelaksana",
        akunKontraktor: "Akun Kontraktor",
        akunPengawas: "Akun Pengawas",
      };
      allowedFields.forEach((key) => {
        if (
          proyekBaru[key] === undefined ||
          proyekBaru[key] === null ||
          proyekBaru[key] === "" ||
          (Array.isArray(proyekBaru[key]) && proyekBaru[key].length === 0)
        ) {
          emptyFields.push(fieldLabels[key] || key);
        } else {
          filteredData[key] = proyekBaru[key];
        }
      });
      if (emptyFields.length > 0) {
        swal(
          "Gagal",
          `Field berikut wajib diisi: ${emptyFields.join(", ")}`,
          "error"
        );
        setIsSubmitting(false);
        return;
      }
      // Format data sesuai permintaan
      const dataToSend = {
        id_admin: pengirim_id,
        nama_proyek: formData.ProyekBaru.nama_proyek,
        tanggal_mulai: formData.ProyekBaru.tanggal_mulai,
        total_anggaran: formData.ProyekBaru.total_anggaran,
        desa: formData.ProyekBaru.desa,
        kecamatan: formData.ProyekBaru.kecamatan,
        longitude: formData.ProyekBaru.longitude,
        latitude: formData.ProyekBaru.latitude,
        radiusInput: formData.ProyekBaru.radiusInput,
        tanggal_selesai: formData.ProyekBaru.tanggal_selesai,
        id_konsultan_perencana: formData.ProyekBaru.konsultan_perencana,
        id_konsultan_pengawas: formData.ProyekBaru.konsultan_pengawas,
        id_kontraktor_pelaksana: formData.ProyekBaru.kontraktor_pelaksana,
        id_akunKontraktor: formData.ProyekBaru.akunKontraktor,
        id_akunPengawas: formData.ProyekBaru.akunPengawas,
        // dokumen tidak dikirim di sini, hanya di FormData
      };

      // Kirim dokumen sebagai FormData (multiple file)
      if (
        formData.ProyekBaru.dokumen &&
        formData.ProyekBaru.dokumen.length > 0
      ) {
        const formDataSend = new FormData();
        // Append field lain
        formDataSend.append("id_admin", pengirim_id);
        formDataSend.append("id_dinas", idDinas);
        formDataSend.append("nama_proyek", formData.ProyekBaru.nama_proyek);
        formDataSend.append("tanggal_mulai", formData.ProyekBaru.tanggal_mulai);
        formDataSend.append(
          "total_anggaran",
          formData.ProyekBaru.total_anggaran
        );
        formDataSend.append("desa", formData.ProyekBaru.desa);
        formDataSend.append("kecamatan", formData.ProyekBaru.kecamatan);
        formDataSend.append("longitude", formData.ProyekBaru.longitude);
        formDataSend.append("latitude", formData.ProyekBaru.latitude);
        formDataSend.append("radiusInput", formData.ProyekBaru.radiusInput);
        formDataSend.append(
          "tanggal_selesai",
          formData.ProyekBaru.tanggal_selesai
        );
        formDataSend.append(
          "id_konsultan_perencana",
          formData.ProyekBaru.konsultan_perencana
        );
        formDataSend.append(
          "id_konsultan_pengawas",
          formData.ProyekBaru.konsultan_pengawas
        );
        formDataSend.append(
          "id_kontraktor_pelaksana",
          formData.ProyekBaru.kontraktor_pelaksana
        );
        formDataSend.append(
          "id_akunKontraktor",
          formData.ProyekBaru.akunKontraktor
        );
        formDataSend.append(
          "id_akunPengawas",
          formData.ProyekBaru.akunPengawas
        );

        // Append dokumen satu per satu
        formData.ProyekBaru.dokumen.forEach((dzFile) => {
          // Pastikan file yang dikirim adalah File asli
          formDataSend.append("dokumen", dzFile.file || dzFile);
        });
        await axios.post(`${API_HOHOLOK}proyek`, formDataSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        // Jika tidak ada file, kirim data biasa
        await axios.post(`${API_HOHOLOK}proyek`, dataToSend);
      }
      swal("Sukses", "Data Proyek baru berhasil dikirim!", "success");
      // Reset formData dan step ke 0
      setFormData({
        ProyekBaru: {
          nama_proyek: "",
          tanggal_mulai: "",
          tanggal_selesai: "",
          total_anggaran: "",
          desa: "",
          kecamatan: "",
          longitude: "",
          latitude: "",
          radiusInput: "",
          konsultan_perencana: null,
          konsultan_pengawas: null,
          kontraktor_pelaksana: null,
          akunKontraktor: null,
          akunPengawas: null,
          dokumen: null,
        },
      });
      setActiveStep(0);
    } catch (err) {
      swal("Gagal", "Gagal mengirim data proyek baru", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => setActiveStep(activeStep + 1);
  const handleBack = () => setActiveStep(activeStep - 1);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox mt={2} mb={3} mx={2}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </MDBox>
              <MDBox p={2}>
                <MDBox>
                  {getStepContent(activeStep, formData, updateStepData)}
                  <MDBox
                    mt={3}
                    width="100%"
                    display="flex"
                    justifyContent="space-between"
                  >
                    {activeStep === 0 ? (
                      <MDBox />
                    ) : (
                      <MDButton
                        variant="gradient"
                        color="light"
                        onClick={handleBack}
                      >
                        back
                      </MDButton>
                    )}
                    <MDButton
                      variant="gradient"
                      color="dark"
                      onClick={!isLastStep ? handleNext : handleSubmit}
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting && isLastStep ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : null
                      }
                    >
                      {isSubmitting && isLastStep
                        ? "Mengirim..."
                        : isLastStep
                        ? "send"
                        : "next"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ProyekBaru;
